import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchJson } from "@/lib/api";

export function useOrders(storeId: string | null, filters: Record<string, string> = {}) {
  const params = new URLSearchParams({ storeId: storeId || "", ...filters });
  return useQuery({
    queryKey: ["orders", storeId, filters],
    queryFn: () => fetchJson<any>(`/api/orders?${params}`),
    enabled: !!storeId,
    refetchOnWindowFocus: "always",
  });
}

export function useOrder(storeId: string | null, orderId: string | null) {
  return useQuery({
    queryKey: ["order", storeId, orderId],
    queryFn: () => fetchJson<any>(`/api/orders/${orderId}?storeId=${storeId}`),
    enabled: !!storeId && !!orderId,
    refetchOnWindowFocus: "always",
  });
}

export function useUpdateOrder(storeId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, ...body }: any) =>
      fetchJson(`/api/orders/${orderId}?storeId=${storeId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["order", storeId, String(variables.orderId)] });
    },
  });
}

export function useBatchUpdateOrders(storeId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: any) =>
      fetchJson(`/api/orders/batch?storeId=${storeId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders"] }),
  });
}

export function useOrderNotes(storeId: string | null, orderId: string | null) {
  return useQuery({
    queryKey: ["orderNotes", storeId, orderId],
    queryFn: () => fetchJson<any>(`/api/orders/${orderId}/notes?storeId=${storeId}`),
    enabled: !!storeId && !!orderId,
  });
}

export function useAddOrderNote(storeId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, ...body }: any) =>
      fetchJson(`/api/orders/${orderId}/notes?storeId=${storeId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["orderNotes", storeId, String(variables.orderId)] });
    },
  });
}

export function useDeleteOrder(storeId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId: number) =>
      fetchJson(`/api/orders/${orderId}?storeId=${storeId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders"] }),
  });
}

export function useSearchProducts(storeId: string | null, search: string) {
  return useQuery({
    queryKey: ["products", storeId, search],
    queryFn: () => fetchJson<any>(`/api/products?storeId=${storeId}&search=${encodeURIComponent(search)}`),
    enabled: !!storeId && search.length >= 2,
  });
}

export function useCreateOrder(storeId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderData: any) =>
      fetchJson(`/api/orders/create?storeId=${storeId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(orderData),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders"] }),
  });
}

export function useCustomers(storeId: string | null, filters: Record<string, string> = {}) {
  const params = new URLSearchParams({ storeId: storeId || "", ...filters });
  return useQuery({
    queryKey: ["customersList", storeId, filters],
    queryFn: () => fetchJson<any>(`/api/customers/list?${params}`),
    enabled: !!storeId,
  });
}

export function useSearchCustomers(storeId: string | null, search: string) {
  return useQuery({
    queryKey: ["customers", storeId, search],
    queryFn: () => fetchJson<any>(`/api/customers?storeId=${storeId}&search=${encodeURIComponent(search)}`),
    enabled: !!storeId && search.length >= 2,
  });
}

export function useShippingMethods(storeId: string | null) {
  return useQuery({
    queryKey: ["shippingMethods", storeId],
    queryFn: () => fetchJson<any>(`/api/shipping-methods?storeId=${storeId}`),
    enabled: !!storeId,
    staleTime: 10 * 60 * 1000,
  });
}

export function usePaymentGateways(storeId: string | null) {
  return useQuery({
    queryKey: ["paymentGateways", storeId],
    queryFn: () => fetchJson<any>(`/api/payment-gateways?storeId=${storeId}`),
    enabled: !!storeId,
    staleTime: 10 * 60 * 1000,
  });
}

export function useValidateCoupon(storeId: string | null, code: string) {
  return useQuery({
    queryKey: ["couponValidate", storeId, code],
    queryFn: () => fetchJson<any>(`/api/coupons/validate?storeId=${storeId}&code=${encodeURIComponent(code)}`),
    enabled: !!storeId && code.length >= 2,
  });
}

export function useSearchProductsKomoran(storeId: string | null, search: string) {
  return useQuery({
    queryKey: ["productsKomoran", storeId, search],
    queryFn: () => fetchJson<any>(`/api/products/search?storeId=${storeId}&search=${encodeURIComponent(search)}`),
    enabled: !!storeId && search.length >= 2,
  });
}

export function useCoupons(storeId: string | null, filters: Record<string, string> = {}) {
  const params = new URLSearchParams({ storeId: storeId || "", ...filters });
  return useQuery({
    queryKey: ["couponsList", storeId, filters],
    queryFn: () => fetchJson<any>(`/api/coupons/list?${params}`),
    enabled: !!storeId,
  });
}

export function useCreateCoupon(storeId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (couponData: any) =>
      fetchJson(`/api/coupons?storeId=${storeId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(couponData),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["couponsList"] }),
  });
}

export function useUpdateCoupon(storeId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ couponId, ...data }: any) =>
      fetchJson(`/api/coupons/${couponId}?storeId=${storeId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["couponsList"] }),
  });
}

export function useDeleteCoupon(storeId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (couponId: number) =>
      fetchJson(`/api/coupons/${couponId}?storeId=${storeId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["couponsList"] }),
  });
}